import { captureException } from '@sentry/nextjs';
import type { Draft } from 'immer';
import produce from 'immer';
import { parse, stringify } from 'superjson';
import { proxy, useSnapshot } from 'valtio';

import type { ZodType, ZodTypeDef } from 'zod';
import { ENVIRONMENT } from '@soundxyz/utils/const';
import { LazyPromise } from '@soundxyz/utils/promise';
import { IS_SERVER, isMainSoundxyzSite } from '@utils/constants';

interface PersistedStoreState<Output> {
  value: Output | null;
  isLoading: boolean;
}

export type PersistedStore<Input, Output> = {
  get: () => Promise<Output | null>;
  set: (value: Input) => Promise<Output | null>;
  state: PersistedStoreState<Output>;
  useStore(): PersistedStoreState<Output>;
  initialValue: Promise<Output | null>;
  clear(): Promise<void>;
  produceExistingState(callback: (draft: Draft<Output>) => void): void | Promise<Output | null>;
};

const PersistedStores: Map<string, PersistedStore<unknown, unknown>> = new Map();

interface PersistenceStorageInput<
  Output = unknown,
  Input = Output,
  Def extends ZodTypeDef = ZodTypeDef,
> {
  /**
   * Parse and validation schema
   */
  schema: ZodType<Output, Def, Input>;
  /**
   * Unique key combination
   */
  key: string;
  /**
   * Should the initial value be eagerly requested?
   */
  eager?: boolean;
}

export function PersistenceStorage<
  Output = unknown,
  Input = Output,
  Def extends ZodTypeDef = ZodTypeDef,
>({
  schema,
  key: idempotentStoreKey,
  eager,
}: PersistenceStorageInput<Output, Input, Def>): PersistedStore<Input, Output> {
  const existingStore = PersistedStores.get(idempotentStoreKey);

  if (existingStore) return existingStore as PersistedStore<Input, Output>;

  const state = proxy<PersistedStoreState<Output>>({
    value: null,
    isLoading: true,
  });

  const key = `sound-${ENVIRONMENT}-${idempotentStoreKey}`;

  async function get(): Promise<Output | null> {
    if (IS_SERVER) return null;

    try {
      const value = localStorage.getItem(key);

      if (value == null) return (state.value = null);

      const parsedValue = await schema.safeParseAsync(parse(value));

      if (parsedValue.success) {
        return (state.value = parsedValue.data);
      } else {
        await localStorage.removeItem(key);
        throw parsedValue.error;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);

      captureException(err, {
        level: 'warning',
        extra: {
          key,
        },
      });

      return (state.value = null);
    } finally {
      if (state.isLoading) state.isLoading = false;
    }
  }

  const initialValue = eager ? get() : LazyPromise(() => get());

  async function setStorage(value: Output) {
    try {
      await localStorage.setItem(key, stringify(value));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);

      captureException(err, {
        level: 'warning',
        extra: {
          key,
        },
      });

      return (state.value = null);
    }

    return value;
  }

  async function set(value: Input): Promise<Output | null> {
    if (IS_SERVER) return null;

    const parsedValue = await schema.parseAsync(value);

    state.value = parsedValue;

    return setStorage(parsedValue);
  }

  async function clear() {
    if (IS_SERVER) return;

    try {
      state.value = null;
      await localStorage.removeItem(key);
    } catch (err) {
      // We ignore browser flaky storage errors

      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  function produceExistingState(
    callback: (draft: Draft<Output>) => void,
  ): void | Promise<Output | null> {
    if (IS_SERVER) return;

    if (state.value == null) {
      if (!isMainSoundxyzSite)
        throw Error(
          `Unexpected empty persisted store state for ${key} - This error only happens on non production website but it's a warning of unexpected logic`,
        );
      return;
    }

    const newState = produce(state.value, callback);

    // Skip persisting if the value didn't change
    if (newState === state.value) return;

    state.value = newState;

    return setStorage(newState);
  }

  const store: PersistedStore<Input, Output> = {
    get,
    set,
    clear,
    initialValue,
    state,
    useStore() {
      return useSnapshot(state);
    },
    produceExistingState,
  };

  PersistedStores.set(idempotentStoreKey, store as PersistedStore<unknown, unknown>);

  return store;
}
